import type { ReactElement, KeyboardEvent } from 'react';
import { useContext, useEffect, useRef } from 'react';

import type {
    ExtendedMaterialSubcategory,
    MaterialSubcategory,
} from '../../gql';

import { HighlightText } from '@bestseller-bit/frontend-community.components.highlight-text';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import { MaterialOnTheFlyValueContext } from './reducer';

type ValueType = ExtendedMaterialSubcategory | MaterialSubcategory | null; // The value can be null when empty or a material subcategory from either extended composition or bom
type MultipleType = false; // We can never select multiple material subcategories in this field
type DisableClearableType = false; // The design does not specify anything regarding clearing
type FreeSoloType = false; // We do not allow free text input, the users must select a real value from the list

interface MaterialSubcategorySectionProps {
    materialSubcategories: Array<
        ExtendedMaterialSubcategory | MaterialSubcategory
    >;
}

const MaterialSubcategorySection = ({
    materialSubcategories,
}: MaterialSubcategorySectionProps): ReactElement => {
    const { materialOnTheFlyValue, dispatchMaterialOnTheFly } = useContext(
        MaterialOnTheFlyValueContext
    );

    const autocompleteRef = useRef<HTMLDivElement>(null);

    const handleChange = (newMaterialSubcategory: ValueType): void => {
        dispatchMaterialOnTheFly({
            type: 'update_material_subcategory',
            payload: { newMaterialSubcategory },
        });
    };

    // Auto focus on mount
    useEffect(() => {
        if (autocompleteRef.current) {
            const input = autocompleteRef.current.querySelector('input');
            if (input) {
                input.focus();
            }
        }
    }, []);

    const handleKeyDown = (event: KeyboardEvent): void => {
        // we only allow Tab to move out of the autocomplete
        if (event.key === 'Tab') {
            return; // Allow normal navigation with Tab
        }

        // For any other key, keep the focus on the autocomplete
        if (
            event.key === 'Enter' &&
            materialOnTheFlyValue.materialSubcategory
        ) {
            // After selecting with Enter, keep the focus
            event.preventDefault();
            if (autocompleteRef.current) {
                const input = autocompleteRef.current.querySelector('input');
                if (input) {
                    setTimeout(() => {
                        input.focus();
                    }, 0);
                }
            }
        }
    };

    return (
        <Autocomplete<
            ValueType,
            MultipleType,
            DisableClearableType,
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments
            FreeSoloType
        >
            value={materialOnTheFlyValue.materialSubcategory}
            options={materialSubcategories}
            getOptionLabel={(option) =>
                option?.materialSubcategoryName ?? 'Unknown'
            }
            isOptionEqualToValue={(option, selectedValue) =>
                option?.materialSubcategoryPublicId ===
                selectedValue?.materialSubcategoryPublicId
            }
            renderOption={({ key, ...props }, option, state) => (
                <li
                    key={key}
                    {...props}
                >
                    <HighlightText
                        text={option?.materialSubcategoryName ?? 'Unknown'}
                        highlight={state.inputValue}
                    />
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Material subcategory"
                    placeholder="Select"
                    required
                    autoFocus
                    onKeyDown={handleKeyDown}
                />
            )}
            blurOnSelect={false}
            fullWidth
            onChange={(_, newValue) => {
                handleChange(newValue);
            }}
        />
    );
};

export { MaterialSubcategorySection };
